import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

// Remember only section identities, never prices/inventory, across navigation.
// Dynamic IDs are learned from the actual product template, not guessed from
// the current collection page or a different product's template.
const SECTION_CACHE_KEY = 'chema-quick-add-sections-v1';
// Share only in-flight HTML, never a long-lived cross-card price/inventory cache.
const productPageRequests = new Map();
const MAX_SPECULATIVE_REQUESTS = 2;
window.addEventListener('pagehide', () => {
  for (const request of productPageRequests.values()) request.controller.abort();
  productPageRequests.clear();
});
function sectionKey(url) {
  const parsed = new URL(url, location.href);
  return `${parsed.origin}${parsed.pathname}?view=${parsed.searchParams.get('view') || ''}`;
}
function readSectionIds() {
  try { return JSON.parse(sessionStorage.getItem(SECTION_CACHE_KEY) || '{}'); } catch { return {}; }
}
function rememberSection(url, sectionId, template) {
  if (window.Shopify?.designMode) return;
  try {
    const entries = Object.entries(readSectionIds()).filter(([, entry]) => Date.now() - entry.time < 1800000);
    const key = sectionKey(url);
    const retained = entries.filter(([entryKey]) => entryKey !== key).slice(-31);
    if (sectionId && template) retained.push([key, { id: sectionId, template, time: Date.now() }]);
    sessionStorage.setItem(SECTION_CACHE_KEY, JSON.stringify(Object.fromEntries(retained)));
  } catch { /* Private mode/storage limits fall back to normal product HTML. */ }
}
function knownSection(url) {
  if (window.Shopify?.designMode) return null;
  const entry = readSectionIds()?.[sectionKey(url)];
  return entry && typeof entry.id === 'string' && typeof entry.template === 'string' &&
    Date.now() - entry.time < 1800000 ? entry : null;
}
// A directly visited PDP also teaches the cache its true main-section identity.
for (const grid of document.querySelectorAll('[data-product-grid-content][data-product-section-id]')) {
  if (new URL(grid.dataset.productUrl || '/', location.href).pathname === location.pathname) {
    rememberSection(location.href, grid.dataset.productSectionId, grid.dataset.productTemplate);
  }
}

export class QuickAddComponent extends Component {
  /** @type {{ url: string, promise: Promise<Document | null>, request: any } | null} */
  #pendingFetch = null;
  #opening = false;
  #openGeneration = 0;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  #cacheTimes = new Map();

  #cacheGrid(url, grid) {
    this.#cachedContent.delete(url);
    this.#cacheTimes.delete(url);
    this.#cachedContent.set(url, grid.cloneNode(true));
    this.#cacheTimes.set(url, Date.now());
    while (this.#cachedContent.size > 4) {
      const oldest = this.#cachedContent.keys().next().value;
      this.#cachedContent.delete(oldest);
      this.#cacheTimes.delete(oldest);
    }
  }

  #readGrid(url) {
    if (Date.now() - (this.#cacheTimes.get(url) || 0) > 60000 || window.Shopify?.designMode) {
      this.#cachedContent.delete(url);
      this.#cacheTimes.delete(url);
    }
    return this.#cachedContent.get(url);
  }
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#releaseRequest();
    this.#openGeneration++;
    this.#opening = false;
    this.querySelector('.quick-add__button[aria-busy]')?.removeAttribute('aria-busy');
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#cacheTimes.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    if (this.#opening) return;
    // Immediate press/rebound feedback while the size-selection dialog loads.
    // This is not a success/checkmark animation: nothing has been added yet.
    const button = event.target instanceof Element ? event.target.closest('.quick-add__button') : null;
    if (button && !button.matches(':disabled, [aria-disabled="true"]') &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches && button.animate) {
      button.getAnimations().filter(animation => animation.id === 'quick-add-press').forEach(animation => animation.cancel());
      const restingScale = getComputedStyle(button).scale;
      const press = button.animate([
        { scale: '0.92' },
        { scale: '1.06', offset: 0.65 },
        { scale: restingScale === 'none' ? '1' : restingScale },
      ], { duration: 280, easing: 'ease-out' });
      press.id = 'quick-add-press';
    }

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.#opening = true;
    const generation = ++this.#openGeneration;
    button?.setAttribute('aria-busy', 'true');
    try {
      let productGrid = this.#readGrid(currentUrl);
      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl, { intent: true });
        // Pointer/focus prefetch, a newer request or a section replacement must
        // never turn a cancelled dialog load into a full-page redirect.
        if (!html || !this.isConnected || generation !== this.#openGeneration) return;
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cacheGrid(currentUrl, productGrid);
        }
      }

      if (!this.isConnected || generation !== this.#openGeneration) return;
      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        if (!this.isConnected || generation !== this.#openGeneration) return;
        this.#updateVariantPicker(productGrid);
        document.querySelector('#quick-add-modal-content variant-picker')?.resetSizeChoice();
      } else {
        // Only a successfully loaded template without purchase markup needs
        // the full product page. An aborted/failed request returned above.
        if (currentUrl) window.location.href = currentUrl;
        return;
      }

      this.#openQuickAddModal();
    } catch (error) {
      // Keep the shopper on the card and allow another click to retry.
      console.warn('[quick-add] Could not open product options:', error);
    } finally {
      if (generation === this.#openGeneration) {
        this.#opening = false;
        button?.removeAttribute('aria-busy');
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  fetchProductPage(productPageUrl, { intent = false } = {}) {
    if (!productPageUrl) return Promise.resolve(null);
    const connection = navigator.connection;
    if (!intent && (document.hidden || this.dataset.usesSellingPlans === 'true' ||
      connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType))) return Promise.resolve(null);
    const cached = this.#readGrid(productPageUrl);
    if (cached) {
      this.#releaseRequest();
      const html = document.implementation.createHTMLDocument('');
      html.body.append(cached.cloneNode(true));
      return Promise.resolve(html);
    }
    // Hover, focus and the actual click share one request. Previously each
    // aborted the other, and the click interpreted that abort as missing HTML.
    if (this.#pendingFetch?.url === productPageUrl && !this.#pendingFetch.request.controller.signal.aborted) {
      if (intent) this.#pendingFetch.request.intent = true;
      return this.#pendingFetch.promise;
    }
    this.#releaseRequest();
    const shared = productPageRequests.get(productPageUrl);
    if (shared && !shared.controller.signal.aborted) {
      if (intent) shared.intent = true;
      return this.#adoptRequest(productPageUrl, shared);
    }
    if (!intent && [...productPageRequests.values()].filter(request => !request.intent && !request.controller.signal.aborted).length >= MAX_SPECULATIVE_REQUESTS) {
      return Promise.resolve(null);
    }

    const controller = new AbortController();
    const entry = { controller, intent, owners: new Set(), promise: null };
    const timeout = setTimeout(() => controller.abort(), 15000);

    const promise = (async () => {
      try {
        const request = async (url) => {
          const response = await fetch(url, { signal: controller.signal, priority: entry.intent ? 'auto' : 'low' });
          if (!response.ok) throw new Error(`Product options response: ${response.status}`);
          const text = await response.text();
          if (controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
          return new DOMParser().parseFromString(text, 'text/html');
        };
        const section = knownSection(productPageUrl);
        let html;
        if (section) {
          const sectionUrl = new URL(productPageUrl, location.href);
          sectionUrl.searchParams.set('section_id', section.id);
          try {
            html = await request(sectionUrl.href);
            const grid = html.querySelector('[data-product-grid-content]');
            const form = grid?.querySelector('product-form-component');
            if (!form || form.dataset.productId !== this.dataset.productId ||
              grid.dataset.productSectionId !== section.id || grid.dataset.productTemplate !== section.template) html = null;
          } catch (error) {
            if (controller.signal.aborted) throw error;
          }
          if (!html) rememberSection(productPageUrl, null);
        }
        // Unknown/changed templates retain merchant-specific block settings.
        if (!html) html = await request(productPageUrl);

        const grid = html.querySelector('[data-product-grid-content]');
        if (grid) {
          const id = grid.getAttribute('data-product-section-id');
          if (id) rememberSection(productPageUrl, id, grid.getAttribute('data-product-template'));
        }
        return html;
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('[quick-add] Product options could not load:', error);
        return null;
      } finally {
        clearTimeout(timeout);
        if (productPageRequests.get(productPageUrl) === entry) productPageRequests.delete(productPageUrl);
      }
    })();
    entry.promise = promise;
    productPageRequests.set(productPageUrl, entry);
    return this.#adoptRequest(productPageUrl, entry);
  }

  #adoptRequest(url, request) {
    const pending = { url, request, promise: null };
    request.owners.add(pending);
    pending.promise = request.promise.then(html => {
      if (this.#pendingFetch !== pending || !this.isConnected) return null;
      const grid = html?.querySelector('[data-product-grid-content]');
      if (grid) this.#cacheGrid(url, grid);
      return html;
    }).finally(() => {
      request.owners.delete(pending);
      if (this.#pendingFetch === pending) this.#pendingFetch = null;
    });
    this.#pendingFetch = pending;
    return pending.promise;
  }

  #releaseRequest() {
    const pending = this.#pendingFetch;
    if (!pending) return;
    this.#pendingFetch = null;
    pending.request.owners.delete(pending);
    if (!pending.request.owners.size) {
      pending.request.controller.abort();
      if (productPageRequests.get(pending.url) === pending.request) productPageRequests.delete(pending.url);
    }
  }

  cancelPrefetch() {
    // Once an actual click adopts it, leaving the button cannot cancel opening.
    if (!this.#opening && !this.#pendingFetch?.request.intent) this.#releaseRequest();
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    {
      // Build only the purchase controls before connecting any fetched content.
      // Desktop and mobile share this compact panel; no gallery is mounted.
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      VariantPicker.prepareQuickAdd(variantPicker);
      const productPrice = productGrid.querySelector('product-price');
      const finishTemplate = productGrid.querySelector('template[data-quick-add-purchase-finish]');
      const purchaseStyles = finishTemplate instanceof HTMLTemplateElement
        ? [...finishTemplate.content.cloneNode(true).childNodes] : [];
      // Block-specific variant colors are emitted alongside the picker, not
      // inside it. Preserve those rules when discarding the full-page layout.
      const variantStyles = [...productGrid.querySelectorAll('style')]
        .filter(style => style.textContent.includes('.variant-picker--custom-'));
      const productTitle = document.createElement('a');
      productTitle.className = 'quick-add-compact-title';
      productTitle.id = 'quick-add-product-title';
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      const productHeaderText = document.createElement('div');
      productHeaderText.classList.add('product-header__text');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        // The fetched price carries product-page typography inline. Let the
        // compact panel's own editor settings control its presentation.
        productPrice.style.setProperty('font-family', 'var(--quick-add-modal-body-font-family)');
        productPrice.style.setProperty('font-size', 'var(--quick-add-modal-body-size)');
        productPrice.style.setProperty('color', 'inherit');
        productPrice.style.setProperty('--text-align', 'center');
        productHeaderText.appendChild(productPrice);
      }
      productHeader.appendChild(productHeaderText);
      if (this.dataset.cardThumbnailSrc) {
        const thumbnailLink = document.createElement('a');
        thumbnailLink.className = 'quick-add-compact-thumbnail';
        thumbnailLink.href = this.productPageUrl;
        thumbnailLink.setAttribute('aria-label', this.dataset.viewProductLabel || 'View product');
        const image = document.createElement('img');
        image.src = this.dataset.cardThumbnailSrc;
        image.alt = this.dataset.cardThumbnailAlt || this.dataset.productTitle || '';
        image.width = 128;
        image.height = 160;
        image.decoding = 'async';
        thumbnailLink.append(image);
        productHeader.insertBefore(thumbnailLink, productHeaderText);
      }

      const viewProductLink = document.createElement('a');
      viewProductLink.className = 'button button-secondary quick-add-mobile-view-product quick-add-summary-view-product';
      viewProductLink.href = this.productPageUrl;
      viewProductLink.textContent = this.dataset.viewProductLabel || 'View product';
      productHeaderText.appendChild(viewProductLink);
      productGrid.replaceChildren(...purchaseStyles, ...variantStyles, productHeader,
        ...[variantPicker, productFormComponent].filter(Boolean));
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    if (modalContent.querySelector('variant-picker[data-size-required]')) return;
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
    for (const select of modalContent.querySelectorAll('variant-picker select')) {
      const option = [...select.options].find((item) => item.dataset.variantId === selectedVariantId);
      if (option && select.value !== option.value) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        for (const link of this.querySelectorAll('.product-header a, .quick-add-mobile-view-product')) {
          if (link instanceof HTMLAnchorElement) link.href = anchorElement.href;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
